import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { userDashboardService } from '../../services/api';

// Async thunks
export const fetchDashboardSummary = createAsyncThunk(
  'userDashboard/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userDashboardService.getSummary();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchUpcomingConsultations = createAsyncThunk(
  'userDashboard/fetchUpcoming',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userDashboardService.getUpcomingConsultations(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchConsultationHistory = createAsyncThunk(
  'userDashboard/fetchHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userDashboardService.getConsultationHistory(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchRecentActivity = createAsyncThunk(
  'userDashboard/fetchActivity',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await userDashboardService.getRecentActivity(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchUserHoroscope = createAsyncThunk(
  'userDashboard/fetchHoroscope',
  async (type = 'daily', { rejectWithValue }) => {
    try {
      const response = await userDashboardService.getUserHoroscope(type);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const userDashboardSlice = createSlice({
  name: 'userDashboard',
  initialState: {
    summary: null,
    upcomingConsultations: [],
    upcomingTotal: 0,
    consultationHistory: [],
    historyTotal: 0,
    recentActivity: [],
    horoscope: null,
    loading: {
      summary: false,
      upcoming: false,
      history: false,
      activity: false,
      horoscope: false
    },
    error: {
      summary: null,
      upcoming: null,
      history: null,
      activity: null,
      horoscope: null
    }
  },
  reducers: {
    clearDashboardErrors: (state) => {
      state.error = {
        summary: null,
        upcoming: null,
        history: null,
        activity: null,
        horoscope: null
      };
    },
    resetDashboard: (state) => {
      state.summary = null;
      state.upcomingConsultations = [];
      state.upcomingTotal = 0;
      state.consultationHistory = [];
      state.historyTotal = 0;
      state.recentActivity = [];
      state.horoscope = null;
      state.error = {
        summary: null,
        upcoming: null,
        history: null,
        activity: null,
        horoscope: null
      };
    }
  },
  extraReducers: (builder) => {
    // Dashboard Summary
    builder
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.loading.summary = true;
        state.error.summary = null;
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.loading.summary = false;
        state.summary = action.payload.data || action.payload;
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        state.loading.summary = false;
        state.error.summary = action.payload;
      });

    // Upcoming Consultations
    builder
      .addCase(fetchUpcomingConsultations.pending, (state) => {
        state.loading.upcoming = true;
        state.error.upcoming = null;
      })
      .addCase(fetchUpcomingConsultations.fulfilled, (state, action) => {
        state.loading.upcoming = false;
        const data = action.payload.data || action.payload;
        state.upcomingConsultations = data.consultations || [];
        state.upcomingTotal = data.total || 0;
      })
      .addCase(fetchUpcomingConsultations.rejected, (state, action) => {
        state.loading.upcoming = false;
        state.error.upcoming = action.payload;
      });

    // Consultation History
    builder
      .addCase(fetchConsultationHistory.pending, (state) => {
        state.loading.history = true;
        state.error.history = null;
      })
      .addCase(fetchConsultationHistory.fulfilled, (state, action) => {
        state.loading.history = false;
        const data = action.payload.data || action.payload;
        state.consultationHistory = data.consultations || [];
        state.historyTotal = data.total || 0;
      })
      .addCase(fetchConsultationHistory.rejected, (state, action) => {
        state.loading.history = false;
        state.error.history = action.payload;
      });

    // Recent Activity
    builder
      .addCase(fetchRecentActivity.pending, (state) => {
        state.loading.activity = true;
        state.error.activity = null;
      })
      .addCase(fetchRecentActivity.fulfilled, (state, action) => {
        state.loading.activity = false;
        const data = action.payload.data || action.payload;
        state.recentActivity = data.activities || [];
      })
      .addCase(fetchRecentActivity.rejected, (state, action) => {
        state.loading.activity = false;
        state.error.activity = action.payload;
      });

    // User Horoscope
    builder
      .addCase(fetchUserHoroscope.pending, (state) => {
        state.loading.horoscope = true;
        state.error.horoscope = null;
      })
      .addCase(fetchUserHoroscope.fulfilled, (state, action) => {
        state.loading.horoscope = false;
        const data = action.payload.data || action.payload;
        state.horoscope = data.horoscope || null;
      })
      .addCase(fetchUserHoroscope.rejected, (state, action) => {
        state.loading.horoscope = false;
        state.error.horoscope = action.payload;
      });
  }
});

export const { clearDashboardErrors, resetDashboard } = userDashboardSlice.actions;

export default userDashboardSlice.reducer;
